// src/components/commonComponent/CommanEditor.js
// Reusable CKEditor wrapper + named getEditorConfig export
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// Named export: shared config factory
export function getEditorConfig({ minHeight = '200px' } = {}) {
  return {
    toolbar: {
      items: [
        'heading', '|',
        'bold', 'italic', 'link',
        'bulletedList', 'numberedList', '|',
        'outdent', 'indent', '|',
        'blockQuote', 'undo', 'redo'
      ]
    },
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
      ]
    },
    // Add more shared config (image, table, etc.) here if needed
    extraPlugins: [],
    // Private property consumed in this module to set editable min-height
    _minHeight: minHeight
  };
}

// Default export: ReusableEditor component
const ReusableEditor = forwardRef(function ReusableEditor(
  { value = '', onChange = () => {}, config = getEditorConfig(), className = '' },
  ref
) {
  const editorInstanceRef = useRef(null);

  useImperativeHandle(ref, () => ({
    // Public API for parent to call
    getData: () => editorInstanceRef.current?.getData?.() ?? '',
    setData: (data) => editorInstanceRef.current?.setData?.(data),
    execute: (commandName, ...args) => {
      const editor = editorInstanceRef.current;
      if (!editor) return;
      // Many built-in actions can be executed via editor.execute('commandName')
      if (typeof editor.execute === 'function') {
        try { editor.execute(commandName, ...args); } catch (e) { /* ignore */ }
      }
    },
    focus: () => editorInstanceRef.current?.editing?.view?.focus?.()
  }), []);

  return (
    <div className={className}>
      <CKEditor
        editor={ClassicEditor}
        data={value}
        config={config}
        onReady={(editor) => {
          editorInstanceRef.current = editor;
          // try to set min-height on editable root if provided
          try {
            const minH = config?._minHeight;
            if (minH) {
              editor.editing.view.change((writer) => {
                writer.setStyle('min-height', minH, editor.editing.view.document.getRoot());
              });
            }
          } catch (err) {
            // ignore if editor structure differs
            // (some custom builds might not expose the same API)
          }
        }}
        onChange={(event, editor) => {
          const data = editor.getData();
          onChange(data, editor);
        }}
        onDestroy={() => { editorInstanceRef.current = null; }}
      />
    </div>
  );
});

export default ReusableEditor;